/**
 * KPI-waarden tonen volgens Kordaro v4: een bekende 0 blijft 0, iets wat niet te berekenen is
 * wordt "—" mét reden. Nooit "NaN", "undefined" of "€ NaN" op het scherm.
 * Puur (geen React) zodat het testbaar is.
 */

export interface KpiWeergave {
  /** Tekst voor het scherm: een geformatteerd getal of "—". */
  tekst: string;
  /** Waarom er geen getal staat (enkel bij "—"). */
  reden?: string;
}

export const ONBEKEND = '—';

/** Een echt, eindig getal (ook als string uit de API), anders null. */
export function alsGetal(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function onbekend(reden: string): KpiWeergave {
  return { tekst: ONBEKEND, reden };
}

export function kpiAantal(v: unknown, reden = 'Niet beschikbaar'): KpiWeergave {
  const n = alsGetal(v);
  return n === null ? onbekend(reden) : { tekst: new Intl.NumberFormat('nl-BE').format(n) };
}

export function kpiEuro(v: unknown, reden = 'Niet beschikbaar', decimalen = 0): KpiWeergave {
  const n = alsGetal(v);
  if (n === null) return onbekend(reden);
  return {
    tekst: new Intl.NumberFormat('nl-BE', {
      style: 'currency', currency: 'EUR', minimumFractionDigits: decimalen, maximumFractionDigits: decimalen,
    }).format(n),
  };
}

/** Percentage dat al door de server berekend is. */
export function kpiProcent(v: unknown, reden = 'Niet beschikbaar'): KpiWeergave {
  const n = alsGetal(v);
  return n === null ? onbekend(reden) : { tekst: `${new Intl.NumberFormat('nl-BE', { maximumFractionDigits: 1 }).format(n)}%` };
}

/**
 * Verhouding teller/noemer. Noemer 0 of onbekend = niet te berekenen (geen 0%):
 * "0 van 0" is geen conversie van 0%, het is "nog niets om te meten".
 */
export function kpiRatio(teller: unknown, noemer: unknown, redenBijNul = 'Nog geen gegevens om te berekenen'): KpiWeergave {
  const t = alsGetal(teller);
  const n = alsGetal(noemer);
  if (t === null || n === null) return onbekend('Niet beschikbaar');
  if (n === 0) return onbekend(redenBijNul);
  return kpiProcent(Math.round((t / n) * 1000) / 10);
}

/** Aantal dagen (bv. gemiddelde oplostijd). */
export function kpiDagen(v: unknown, reden = 'Niet beschikbaar'): KpiWeergave {
  const n = alsGetal(v);
  return n === null ? onbekend(reden) : { tekst: `${new Intl.NumberFormat('nl-BE', { maximumFractionDigits: 1 }).format(n)} d` };
}

/** Vangnet voor bestaande aanroepen: wat er ook binnenkomt, nooit "NaN"/"undefined" tonen. */
export function normaliseerKpi(waarde: string | number | KpiWeergave | null | undefined): KpiWeergave {
  if (waarde && typeof waarde === 'object') return waarde;
  if (waarde === null || waarde === undefined) return onbekend('Niet beschikbaar');
  if (typeof waarde === 'number') {
    return Number.isFinite(waarde) ? { tekst: new Intl.NumberFormat('nl-BE').format(waarde) } : onbekend('Niet te berekenen');
  }
  if (/NaN|undefined|null|Infinity/.test(waarde) || waarde.trim() === '' || waarde.trim() === '-') {
    return onbekend('Niet beschikbaar');
  }
  return { tekst: waarde };
}
